import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, apiClient } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Loader2, AlertCircle, Mail, ArrowLeft } from 'lucide-react';

const LOGIN_HERO_IMAGE = 'https://customer-assets-eiarnc6j.emergentagent.net/job_39ce99f8-2df7-4a5a-9cad-1b3f94f84fe9/artifacts/21ukqol2_image.png';

const BrandLogo = () => (
  <Link to="/" className="flex items-center gap-3 mb-10" data-testid="login-brand-logo">
    <div className="w-11 h-11 bg-[#EFB342] rounded-xl flex items-center justify-center">
      <span className="text-black font-heading font-bold text-xl">Rx</span>
    </div>
    <div>
      <h1 className="font-heading text-xl font-bold text-stone-900 tracking-wide leading-none">RxGRIT</h1>
      <p className="text-[10px] text-stone-500 uppercase tracking-widest mt-1">by BUILD Nutrition</p>
    </div>
  </Link>
);

const HeroPanel = () => (
  <div className="hidden lg:flex flex-1 items-center justify-center p-10 bg-[#EEEAE1] border-l border-stone-200">
    <div className="relative w-full max-w-lg">
      <img
        src={LOGIN_HERO_IMAGE}
        alt="RxGRIT member at sunset — knowledge, habits, real change"
        className="w-full aspect-[4/5] object-cover rounded-3xl shadow-xl"
        style={{ filter: 'saturate(0.95) brightness(1.02)' }}
        data-testid="login-hero-image"
      />
      <div className="absolute bottom-5 left-5 right-5 md:left-auto md:right-6 md:max-w-xs bg-[#F5F4F1]/95 backdrop-blur-sm rounded-xl px-4 py-3 border border-stone-200 shadow-md">
        <p className="text-sm text-stone-800 leading-snug mb-1.5 italic">
          &ldquo;I love that it&apos;s all about taking small steps consistently. It works.&rdquo;
        </p>
        <p className="text-[#B8871E] font-semibold text-[11px] uppercase tracking-[0.15em]">— Katharine K.</p>
      </div>
    </div>
  </div>
);

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [showSuspendedMessage, setShowSuspendedMessage] = useState(false);
  const [showTrialExpiredMessage, setShowTrialExpiredMessage] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setErrorMessage('Please fill in all fields');
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setShowSignUpPrompt(false);
    setShowSuspendedMessage(false);
    setShowTrialExpiredMessage(false);

    try {
      const user = await login(email, password, rememberMe);
      toast.success(`Welcome back, ${user.name}!`);
      setTimeout(() => {
        navigate(user.is_admin ? '/admin' : '/dashboard');
      }, 100);
    } catch (err) {
      let detail = err.response?.data?.detail || 'Invalid email or password. Please try again.';

      if (detail.startsWith('SUSPENDED:')) {
        detail = detail.replace('SUSPENDED:', '');
        setShowSuspendedMessage(true);
      } else if (detail.startsWith('TRIAL_EXPIRED:')) {
        detail = detail.replace('TRIAL_EXPIRED:', '');
        setShowTrialExpiredMessage(true);
      }

      setErrorMessage(detail);

      if (detail.toLowerCase().includes('no account found')) {
        setShowSignUpPrompt(true);
      }

      toast.error(detail.replace('https://rxgrit.buildnutrition.ca/signup', 'the signup page'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setSendingReset(true);
    try {
      await apiClient.post('/auth/forgot-password', { email: forgotEmail });
      setResetSent(true);
      toast.success('Password reset link sent! Check your email.');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send reset link';
      toast.error(detail);
    } finally {
      setSendingReset(false);
    }
  };

  // Shared input classes
  const inputCls = 'bg-white border-stone-300 focus:border-[#EFB342] focus:ring-1 focus:ring-[#EFB342] text-stone-900 placeholder:text-stone-400 h-12 lg:h-14 text-base lg:text-lg';
  const labelCls = 'text-stone-700 text-base lg:text-lg';

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-[#F5F4F1] flex text-stone-900">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <BrandLogo />

            {resetSent ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-[#EFB342]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail className="text-[#B8871E]" size={32} />
                </div>
                <h2 className="font-heading text-2xl lg:text-3xl font-bold text-stone-900 mb-3 uppercase tracking-tight">Check your email</h2>
                <p className="text-stone-600 text-base lg:text-lg mb-6 leading-relaxed">
                  We&apos;ve sent a password reset link to <strong className="text-stone-900">{forgotEmail}</strong>
                </p>
                <p className="text-stone-500 text-sm mb-8">
                  Didn&apos;t receive the email? Check your spam folder or try again.
                </p>
                <Button
                  onClick={() => { setShowForgotPassword(false); setResetSent(false); setForgotEmail(''); }}
                  className="bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold uppercase tracking-wider h-12 text-base rounded-full px-6"
                >
                  <ArrowLeft className="mr-2" size={20} />
                  Back to login
                </Button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setShowForgotPassword(false)}
                  className="flex items-center gap-2 text-stone-500 hover:text-stone-900 mb-8 transition-colors"
                >
                  <ArrowLeft size={20} />
                  <span className="text-base">Back to login</span>
                </button>

                <div className="mb-8">
                  <h2 className="font-heading text-2xl lg:text-3xl font-bold text-stone-900 mb-3 uppercase tracking-tight">Forgot password?</h2>
                  <p className="text-stone-600 text-base lg:text-lg leading-relaxed">
                    Enter your email and we&apos;ll send you a link to reset your password.
                  </p>
                </div>

                <form onSubmit={handleForgotPassword} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="forgotEmail" className={labelCls}>Email address</Label>
                    <Input
                      id="forgotEmail"
                      type="email"
                      placeholder="you@example.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className={inputCls}
                      data-testid="forgot-email-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={sendingReset}
                    className="w-full bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold uppercase tracking-wider h-14 text-base lg:text-lg rounded-full"
                    data-testid="forgot-submit-btn"
                  >
                    {sendingReset ? (
                      <Loader2 className="animate-spin" size={24} />
                    ) : (
                      <>
                        Send reset link
                        <Mail className="ml-2" size={20} />
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>

        <HeroPanel />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F4F1] flex text-stone-900">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <BrandLogo />

          <div className="mb-8">
            <p className="text-[#EFB342] uppercase tracking-[0.2em] text-xs font-semibold mb-3">Member login</p>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-stone-900 mb-3 uppercase tracking-tight leading-[1.05]">
              Welcome back.
            </h2>
            <p className="text-stone-600 text-base lg:text-lg leading-relaxed">
              Sign in to continue your daily practice.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className={labelCls}>Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className={labelCls}>Password</Label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm lg:text-base text-[#B8871E] hover:underline font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputCls} pr-12`}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
                >
                  {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                </button>
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-5 h-5 rounded border-stone-300 bg-white text-[#EFB342] focus:ring-[#EFB342] focus:ring-offset-0 cursor-pointer"
                data-testid="remember-me-checkbox"
              />
              <label htmlFor="rememberMe" className="text-base text-stone-600 cursor-pointer select-none">
                Stay logged in for 30 days
              </label>
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div className={`${showSuspendedMessage || showTrialExpiredMessage ? 'bg-[#EFB342]/10 border-[#EFB342]/40' : 'bg-red-50 border-red-200'} border rounded-2xl p-4`} data-testid="login-error-message">
                <div className="flex items-start gap-3">
                  <AlertCircle className={showSuspendedMessage || showTrialExpiredMessage ? 'text-[#B8871E]' : 'text-red-500'} size={24} />
                  <div>
                    {showSuspendedMessage ? (
                      <>
                        <p className="text-stone-900 text-base font-medium">Your subscription has expired</p>
                        <div className="mt-3">
                          <p className="text-stone-600 text-base">
                            Please renew your subscription to regain access to RxGRIT.
                          </p>
                          <a
                            href="https://rxgrit.buildnutrition.ca/signup"
                            className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-[#EFB342] text-black font-semibold rounded-full hover:bg-[#D99F30] transition-colors"
                          >
                            Renew subscription
                            <ArrowRight size={18} />
                          </a>
                        </div>
                      </>
                    ) : showTrialExpiredMessage ? (
                      <>
                        <p className="text-stone-900 text-base font-medium">Your free trial has ended</p>
                        <div className="mt-3">
                          <p className="text-stone-600 text-base">
                            Ready to continue your daily practice? Sign up now to keep your progress.
                          </p>
                          <a
                            href="https://rxgrit.buildnutrition.ca/signup"
                            className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-[#EFB342] text-black font-semibold rounded-full hover:bg-[#D99F30] transition-colors"
                          >
                            Sign up now
                            <ArrowRight size={18} />
                          </a>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-red-600 text-base font-medium">{errorMessage}</p>
                        {showSignUpPrompt && (
                          <div className="mt-3">
                            <p className="text-stone-600 text-base">
                              Don&apos;t have an account yet?{' '}
                              <a
                                href="https://buildnutrition.ca/rxgrit"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#B8871E] hover:underline font-medium"
                              >
                                Click here to sign up →
                              </a>
                            </p>
                            <p className="text-stone-500 text-sm mt-2">
                              Or{' '}
                              <Link to="/free-trial" className="text-[#B8871E] hover:underline">
                                try our 7-day free trial
                              </Link>
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#EFB342] text-black hover:bg-[#D99F30] font-bold uppercase tracking-wider h-12 lg:h-14 text-base lg:text-lg rounded-full"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={24} />
              ) : (
                <>
                  Sign in
                  <ArrowRight className="ml-2" size={22} />
                </>
              )}
            </Button>
          </form>

          <p className="mt-8 mb-16 text-center text-stone-500 text-base lg:text-lg">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-[#B8871E] hover:underline font-semibold">
              Get started
            </Link>
          </p>
        </div>
      </div>

      <HeroPanel />
    </div>
  );
};

export default LoginPage;
